// @givmo/board — EVERY word the calendar's screens add, in one place (CBG-CAL1 slice 1d).
//
// ══ WHICH WORDS ARE APPROVED ════════════════════════════════════════════════════════════════════
//
// All of them. Jonathan approved the CAL1-WEB mockups on 2026-10-05 ("looks good."; question 10
// "yes"): the words the frames on that review page SHOW, verbatim, and the other number of a drawn
// line ("Move 1 meeting" beside the drawn "Move 2 meetings"; the orchestrator, 2026-10-07). Every
// other line here is marked `Approved (CBG-CAL1-COPY1)`: it shipped DRAFT under the orchestrator's
// rulings of 2026-10-07 (the build's questions file, rounds 1 to 3) and was on copy round
// CBG-CAL1-COPY1's page, which Jonathan approved as written on 2026-10-07 22:45 PDT ("approve
// all"). A new line no approved page shows ships `DRAFT` until he rules on it.
//
// Then design-225's copy page (`scratchpad/2026-10-17-prodfixes-design/copy-review.html`), approved
// as recommended on 2026-10-17 22:51 PDT ("approve"): the people an invitation goes to are every
// serving seat of the board, or of the committee that meets, directors or not (D-CBG-21), so its
// lines say "everyone invited" (CBG-FIX42); and a move that didn't finish says so (CBG-FIX36). Each
// line it changed or added is marked `Approved (CBG-FIX42, row …)` or `Approved (CBG-FIX36, row …)`.
// The keys keep their names (`factDirectorsOwner`, `directorsTold`): no one reads them.
//
// Then CBG-FIX111's copy page (`scratchpad/2026-10-18-fix111-copy-round/copy-review.html`), approved
// as recommended on 2026-10-18 17:23 PDT ("approved"): CBG-CAL5's line on the connect screen (row
// 3.1), and the handover's title and first line when the team list no longer names the departed
// officer (CBG-FIX116, rows 3.2a and 3.2b), each marked `Approved (CBG-FIX111, row …)`.
//
// Then D-CBG-50 (Jonathan, in-chat 2026-10-31 08:06 PDT, "I agree with your recs, please handle
// it"): the moved meeting's words as built (CBG-FIX181), its line, its quiet line and its Fix
// dialog, the dialog's button becoming "Stop updating it". Each is marked `Approved (D-CBG-50)`.
//
// Then CBG-UX14, Board's calendar settings in one place (`scratchpad/2026-11-01-ux14-calendar-
// settings-design.md`): the words drawn on its frames, approved as drawn on 2026-11-01 15:11 PST
// (Q5, "yes"), and the lines the rulings added, approved 15:23 PST and 15:37 PST (D-CBG-55). Each
// still in use is marked `Approved (CBG-UX14, …)`.
//
// Then D-CBG-56: Jonathan replaced that card with a simpler one ("give it a try", 2026-11-01 17:09
// PST) and approved it as rendered at 19:24 PST ("ok then i'm good with it"; the renders are in
// `scratchpad/2026-11-01-ux14-calendar-settings-design/simple-approved/`). Its lines are marked
// `Approved (CBG-UX14, D-CBG-56)`. The drawn card's answer, its rows and their lines retired with it.
// Its extension (Jonathan, 2026-11-01 20:56 PST, "ok": the line for one's own calendar Board stopped
// using when one's access ended) is marked `Approved (CBG-UX14, D-CBG-56's extension, …)`.
//
// ⚠ No pronoun for a named person: the product can't know anyone's pronouns, so a line names the
// person or the address again, never "her calendar". No possessive on an address, and no address in
// a dialog title, but the one approved exception, "Disconnect {email}?" (B10b). Plan §3.2 and §3.4
// bind every line, the fine print included: what Board did and what it can't do, never a conclusion
// about notice or a record.

/** A provider as people know it. */
const PROVIDER_NAMES: Readonly<Record<string, string>> = {
  google_calendar: 'Google Calendar',
  microsoft_calendar: 'Outlook',
};

/** The provider's name for a line ("Google Calendar"), or the calendar's for one this build doesn't name. */
export function providerName(provider: string | null | undefined): string {
  return (
    PROVIDER_NAMES[provider ?? ''] ??
    // Approved (CBG-CAL1-COPY1): an unknown provider (only the hidden `test` provider, dev e2e). R2-5.
    'the calendar'
  );
}

/** "1 upcoming meeting" / "2 upcoming meetings". */
function meetingsCount(count: number): string {
  return `${count} upcoming meeting${count === 1 ? '' : 's'}`;
}

/**
 * "1 person" / "7 people": everyone the invitation went to (CBG-FIX42, row 3.1). A no-break space
 * keeps the number with its noun, so a phone never ends a line on "7".
 */
function people(count: number): string {
  return count === 1 ? '1\u00a0person' : `${count}\u00a0people`;
}

/** The neutral invitation title the backend composes for a regular meeting. */
export function neutralTitle(orgName: string): string {
  return `${orgName}, regular board meeting`;
}

/**
 * The board-account example address: `board@` and the domain of an address the organization uses —
 * the officer's own, or another connected calendar's — never a personal mail service's.
 */
export function boardAddressExample(domain: string | null): string {
  return domain !== null
    ? `board@${domain}`
    : // Approved (CBG-CAL1-COPY1): no organization domain to borrow (the derivation's example). R2-3.
      'board@yourorg.org';
}

export const calendarWords = {
  // ── The connect screen, before Google's consent screen ──────────────────────────────────────────
  connectTitle: 'Send board invitations from your calendar',
  // Approved (CBG-FIX42, row 3.3).
  connectLede:
    'Board meetings you schedule go on your calendar, and the invitations come from you.',
  connectBoardTitle: 'Connect a board account',
  connectBoardLede: (example: string) =>
    `Meetings go out from an account your organization keeps for the board, such as ${example}, so they stay in place when officers change.`,
  choiceMine: 'Your calendar',
  choiceMineDescription: 'Meetings you schedule go out from you.',
  choiceBoard: 'A board account',
  choiceBoardDescription: (example: string) =>
    `Such as ${example}. Best when several people schedule meetings, or when officers change.`,
  // Approved (CBG-CAL1-COPY1): the choice group's accessible name, never shown. R2-4.
  choiceGroup: 'Whose calendar',
  factAccess:
    'Google will ask to let Givmo see and edit events in your calendars. Board opens only the board meetings it schedules, and never reads their titles or notes.',
  factAccessBoard:
    'Google will ask to let Givmo see and edit events in that account’s calendars. Board opens only the board meetings it schedules, and never reads their titles or notes.',
  // Approved (CBG-FIX111, row 3.1): CBG-CAL5's line, told to the officer before they connect, on
  // both ways in (agreed 2026-10-17 21:45 PDT, the calendar pages' question 1). Google says this
  // disclosure "Cannot be placed only in a privacy policy". The public Givmo Board page's approved
  // line, word for word.
  factAssistant:
    'If someone in your organization connects their own AI assistant to Givmo, it can read a meeting’s time, place and video link, and which calendar sent it, when that person asks.',
  // Approved (CBG-FIX42, row 3.4): an Owner's.
  factDirectorsOwner:
    'Everyone invited can change a meeting from their own calendar, see the guest list, and invite others. Board follows each change. You can turn this off in Board settings.',
  // Approved (CBG-FIX42, row 3.5): any other officer's.
  factDirectorsOfficer:
    'Everyone invited can change a meeting from their own calendar, see the guest list, and invite others. Board follows each change.',
  howBoardSignIn: 'Sign in to Google as the board account, not as yourself.',
  continueGoogle: 'Continue with Google',
  // Approved (CBG-CAL1-COPY1): behind "How this works", collapsed in the frames. B1.
  howTitle: (orgName: string) =>
    `Invitations are titled “${neutralTitle(orgName)}”, not with the meeting’s title in Board.`,
  // Approved (CBG-CAL1-COPY1): behind "How this works" (the plan's call 1 in its last sentence). B1.
  howCount:
    'If a meeting’s time changes in a calendar, Board recounts how many days ahead it was scheduled, against the notice period you recorded. It counts from when the calendar recorded the change.',
  // Approved (CBG-CAL1-COPY1): behind "How this works", one's own calendar only. B1.
  howDelegates: 'People who manage your calendar can see and change board meetings on it.',
  // Approved (CBG-FIX42, row 3.6): behind "How this works", one's own calendar only.
  howHandover:
    'If your access to Board ends, the next officer moves your upcoming board meetings to their calendar. Everyone invited gets one cancellation and one new invitation.',

  // ── The return from Google ───────────────────────────────────────────────────────────────────
  connectedTitle: 'Your calendar is connected',
  connectedBody: (email: string) => `Board meetings you schedule go out from ${email}.`,
  // Approved (CBG-CAL1-COPY1): the board account's return; connect-done draws one's own calendar. R2-1.
  connectedBoardTitle: 'The board account is connected',
  // Approved (CBG-CAL1-COPY1): as above. R2-1.
  connectedBoardBody: (email: string) => `Meetings can now go out from ${email}.`,
  /** The plan's call 4, verbatim. */
  personalAccount:
    'This looks like a personal account. A board account your organization controls keeps meetings in place when people change.',
  notConnectedTitle: 'Your calendar isn’t connected',
  refused: 'Google didn’t give Givmo access, so nothing changed.',
  expired: 'The connection took too long, or was started in another window, so nothing changed.',
  // Approved (D-CBG-49, CBG-FIX182): Google's box starts unticked for every account outside the
  // project's own organization, so the line asks the person to tick it.
  unticked:
    'Google left out calendar access. On Google’s screen, tick “See, create, change, and delete events on Google calendars you own”.',
  workspaceBlocked:
    'Your Google Workspace administrator hasn’t allowed Givmo to use Google Calendar. Ask them to allow it, then try again.',
  /** Core's typed refusals on `complete` with no drawn frame (B6). */
  notConnected: {
    // Approved (CBG-CAL1-COPY1): the backend's own sentence for a reconnect that picked another account. B6.
    different_account: 'That’s a different account. Use Move meetings instead.',
    // Approved (CBG-CAL1-COPY1): B6.
    account_already_connected: 'Another officer has already connected that account.',
    // Approved (CBG-CAL1-COPY1): B6.
    personal_connection_exists:
      'You’ve already connected a calendar. Disconnect it first to use another.',
    // Approved (CBG-CAL1-COPY1): B6.
    board_account_exists: 'Your organization already has a board account.',
    // Approved (CBG-CAL1-COPY1): B6; also oauth_exchange_failed and redirect_uri_not_allowed.
    no_refresh_token: 'Google didn’t finish connecting. Try again.',
    // Approved (CBG-CAL1-COPY1): B6.
    provider_busy: 'Google Calendar is busy right now. Try again in a minute.',
  } as Readonly<Record<string, string>>,
  tryAgain: 'Try again',
  startAgain: 'Start again',
  done: 'Done',
  close: 'Close',
  cancel: 'Cancel',

  // ── Your settings → Calendar ───────────────────────────────────────────────────────────────────
  // One's own calendar is one connection, the person's own, for any Givmo app that uses one
  // (CBG-UX14's Q3, 2026-11-01 15:14 PST), so the card's words name no app; its row names each app
  // that uses it ("Board · 1 upcoming meeting: Nov 17").
  yourTitle: 'Calendar',
  // Approved (CBG-UX14, D-CBG-55, 15:23 PST): nothing connected.
  yourLedeNone:
    'Connect your Google Calendar once, and every Givmo app that uses a calendar can use it.',
  // Approved (CBG-UX14, D-CBG-55 as extended, 15:37 PST): connected.
  yourLedeConnected: 'Your Google Calendar, which every Givmo app that uses a calendar can use.',
  // Approved (CBG-UX14, Q5): the app the connection serves, first in its row's facts.
  usedByBoard: 'Board',
  connectYours: 'Connect your calendar',
  disconnect: 'Disconnect',
  reconnect: 'Reconnect',
  needsReconnecting: 'Needs reconnecting',
  reconnectLine:
    'Google stopped letting Board use this calendar. Until you reconnect, changes made in either place don’t reach the other.',
  /** "1 upcoming meeting: Oct 20"; the days only where the meetings list has them. */
  upcoming: (count: number, days: readonly string[]) =>
    count === 0
      ? 'No upcoming meetings'
      : days.length === 0
        ? meetingsCount(count)
        : `${meetingsCount(count)}: ${days.join(', ')}`,

  // ── Settings → Board → Calendar: the one card (CBG-UX14, D-CBG-56) ──────────────────────────────
  //
  // One main line and one act, by what sends the reader's own meetings, with a small line under it
  // where one applies; then an Owner's Move line, and Invitation options.
  // Approved (CBG-UX14, D-CBG-56): the card's title.
  boardTitle: 'Calendar',
  // Approved (CBG-UX14, D-CBG-56): nothing sends the reader's meetings (no board account, and their
  // own calendar isn't connected).
  connectToSend: 'Connect a Google Calendar, and Board sends meeting invitations from it.',
  // Approved (CBG-UX14, Q5): the act beside a line that already names what it connects.
  connect: 'Connect',
  // Approved (CBG-UX14, D-CBG-56): under it, while Givmo's email is on.
  untilGivmo: 'Until you do, Givmo emails the invitations for you.',
  // Approved (CBG-UX14, D-CBG-56): the same, while Givmo's email is off.
  untilNobody: 'Until you do, each person adds meetings to their own calendar from Board.',
  // Approved (CBG-UX14, D-CBG-56): a calendar sends them, the board account if one is connected.
  sentFromAddress: (address: string) => `Invitations are sent from ${address}.`,
  // Approved (CBG-UX14, D-CBG-56): beside the board account, the reader's own connected calendar,
  // with a small Disconnect after it.
  ownAlsoChoosable: (address: string) =>
    `You can send a meeting from your own calendar (${address}) when you schedule it.`,
  // Approved (CBG-UX14, D-CBG-56): beside the board account, while the reader's own isn't connected
  // (the schedule dialog doesn't offer to connect it while a board account sends).
  connectOwnLink: 'Connect your own calendar',
  // Approved (CBG-UX14, D-CBG-56): under an Owner's own calendar while it sends.
  useBoardAccountLink: 'Use a shared board account instead',
  // Approved (CBG-UX14, D-CBG-56): the calendar that sends them needs reconnecting (in amber).
  stoppedUsing: (address: string) =>
    `Google stopped letting Board use ${address}. Reconnect it so invitations stay up to date.`,
  // Approved (CBG-UX14, D-CBG-56's extension, 2026-11-01 20:56 PST): in its place for the reader's
  // own calendar left `organizer_left` when their access to Board ended (core keeps it so after they
  // regain access, until a reconnect); Google did nothing. A board account never reaches that status.
  accessEnded: (address: string) =>
    `Board stopped using ${address} when your access to Board ended. Reconnect it to send invitations from it again.`,
  // Approved (CBG-UX14, D-CBG-56): in place of Reconnect, for a reader without the Owner string.
  ownerMustReconnect: 'An Owner of Board needs to reconnect it.',
  // Approved (CBG-UX14, Q4 and Q5): under a board account that needs reconnecting, who connected it.
  connectedBy: (name: string, day: string) => `${name} connected it on ${day}.`,
  // Approved (CBG-UX14, Q4): the same, when it was the reader.
  connectedByYou: (day: string) => `You connected it on ${day}.`,
  // Approved (CBG-UX14, D-CBG-56): last under it, for every reader, while Givmo's email is on.
  untilReconnectedGivmo: 'Until it’s reconnected, Givmo emails the invitations for you.',
  // Approved (CBG-UX14, D-CBG-56): the same, while Givmo's email is off.
  untilReconnectedNobody:
    'Until it’s reconnected, each person adds meetings to their own calendar from Board.',
  /**
   * Approved (CBG-UX14, D-CBG-56): an Owner's only, while an officer who left still sends upcoming
   * meetings. It names the address, never "her calendar"; with no name, Board's words for a name it
   * can't show (CBG-FIX111, row 3.2b).
   */
  leftWithMeetings: (name: string | null, count: number, email: string) =>
    `${name ?? 'Someone whose name isn’t shown'} left, and ${
      count === 1 ? '1 upcoming meeting still goes' : `${count} upcoming meetings still go`
    } out from ${email}.`,
  // Approved (CBG-UX14, D-CBG-56): its act, by the count.
  moveIt: 'Move it',
  // Approved (CBG-UX14, D-CBG-56): as above.
  moveThem: 'Move them',
  // Approved (CBG-UX14, D-CBG-56): below a divider, while a Google calendar sends the reader's
  // meetings, even one that needs reconnecting.
  invitationOptions: 'Invitation options',
  // Approved (CBG-UX14, D-CBG-56): the two guest settings as one sentence. Three, not four: guests
  // can change a meeting only while they can see who's invited (the backend refuses the pair).
  optionsBoth: 'Guests can change the meeting and see who’s invited.',
  // Approved (CBG-UX14, D-CBG-56): as above.
  optionsSeeOnly: 'Guests can see who’s invited, but can’t change the meeting.',
  // Approved (CBG-UX14, D-CBG-56): as above.
  optionsNeither: 'Guests can’t change the meeting or see who’s invited.',
  // Approved (CBG-UX14, Q5): in place of Change, for a reader without the Owner string.
  ownersChange: 'Owners of Board can change these.',
  /** "How this works" at the card's foot, in this order. */
  boardHow: [
    // Approved (CBG-UX14, D-CBG-56): D-CBG-55's order line, without its "by email from Givmo" clause.
    'Once a board account is connected, every officer’s meetings go out from it, unless an officer chooses their own calendar for a meeting when scheduling it. Without one, an officer who has connected their own calendar sends from it.',
    // Approved (CBG-CAL1-COPY1): B1.
    'Each meeting remembers the calendar it went out from, and every officer’s change in Board goes out through that calendar.',
    // Approved (CBG-CAL1-COPY1): B1.
    'A board account stays when officers change. A personal calendar goes with its officer: when someone’s access to Board ends, the next officer moves their meetings.',
    // Approved (CBG-FIX42, row 3.9).
    'A change applies to meetings scheduled after it. You can then apply it to upcoming meetings without emailing anyone.',
    // Approved (CBG-CAL1-COPY1): B1.
    'A change made in a calendar comes back to Board: the time, the place, the video link, or a cancellation. Titles and notes never do.',
  ],

  // ── A calendar's row (Connected accounts) and the Move dialog's act ────────────────────────────
  boardAccount: 'Board account',
  noLongerHasAccess: 'No longer has access',
  // Approved (CBG-CAL1-COPY1): a row's "⋯" accessible name, never shown (the roster's own pattern). R2-4.
  rowActions: (name: string) => `Actions for ${name}`,
  moveMeetings: 'Move meetings',
  personalGoogle: 'personal Google account',

  // ── The Change dialog: what invitations show and allow ────────────────────────────────────────
  invitationsTitle: 'Calendar invitations',
  // Approved (CBG-FIX42, row 3.10).
  modifyLabel: 'Everyone invited can change meetings from their calendar',
  modifyDescription:
    'They can move a meeting, change its place or video link, and invite others. Board follows each change and notes it in the meeting’s history.',
  // Approved (CBG-FIX42, row 3.11).
  listLabel: 'Everyone invited sees the guest list',
  listDescription: 'Google shows the guest list to anyone who can change a meeting.',
  listOffNote:
    'Turning this off also turns off changing meetings from their calendar, because Google shows the guest list to anyone who can change a meeting.',
  titleLabel: 'Invitation title',
  titleNeutralValue: (orgName: string) => `“${neutralTitle(orgName)}”`,
  titleNeutralDescription: 'Givmo’s default: the meeting’s type, never its title.',
  titleBoard: 'The meeting’s title in Board',
  // Approved (CBG-FIX42, row 3.12), both forms.
  titleBoardDescription: (exampleTitle: string | null) =>
    exampleTitle === null
      ? 'Anyone who can see an invited person’s calendar sees the title.'
      : `Such as “${exampleTitle}”. Anyone who can see an invited person’s calendar sees the title.`,
  change: 'Change',
  appliesFromNow: 'Applies to meetings scheduled from now on.',
  save: 'Save',
  applyLine: (count: number) =>
    `Your ${meetingsCount(count)} still ${count === 1 ? 'has' : 'have'} the earlier settings.`,
  applyButton: 'Apply to them',
  // Approved (CBG-FIX42, row 3.13).
  applyNote: 'No one gets an email about it.',
  // Approved (CBG-CAL1-COPY1): Apply's toast (the README's build note). B7.
  applied: (count: number) => `Applied to ${count} meeting${count === 1 ? '' : 's'}`,
  // Approved (CBG-CAL1-COPY1): Apply's toast when some meetings need attention. B7.
  appliedPartly: (applied: number, attention: number) =>
    `${applied} applied; ${attention} need${attention === 1 ? 's' : ''} attention`,

  // ── Settings → Organization → Connected accounts (a Global Admin's) ────────────────────────────
  orgTitle: 'Connected accounts',
  orgLede: 'Accounts outside Givmo that your organization lets Givmo use.',
  orgFor: 'Board meetings',
  orgDisconnectTitle: (email: string) => `Disconnect ${email}?`,
  /** The drawn body's first sentence; the second (`sendsNoMeetings`) only when Board could say so. */
  orgDisconnectUntil: (name: string) =>
    `Board meetings can’t go out from this calendar until ${name} connects it again.`,
  sendsNoMeetings: 'It sends no upcoming meetings.',
  // Approved (CBG-CAL1-COPY1): one's own calendar that sends no meetings, from Your settings. B10a.
  ownDisconnectUntil: 'Board meetings can’t go out from this calendar until you connect it again.',
  // Approved (CBG-CAL1-COPY1): the board account, which no one person reconnects. R3-1.
  boardDisconnectUntil:
    'Board meetings can’t go out from this calendar until it’s connected again.',

  // ── The schedule dialog ────────────────────────────────────────────────────────────────────────
  invitationsLabel: 'Invitations',
  fromMine: 'From your calendar',
  fromBoard: 'From the board account',
  fromGivmo: (orgName: string) => `By email from Givmo, as ${orgName} Board`,
  // Approved (CBG-FIX42, row 3.14): nothing chosen to send (no frame draws it).
  notSentLine: 'Not sent. Everyone can add the meeting to their calendar from Board.',
  sendFromAnother: 'Send from another calendar',
  connectInline: 'Connect your calendar',
  // Approved (CBG-CAL1-COPY1): the officer's own calendar needs reconnecting (D-5). B5.
  reconnectInline: 'Reconnect your calendar',
  connectInlineTail: 'to send them from you.',
  // Approved (CBG-CAL1-COPY1): the picker group's accessible name, never shown. R2-4.
  pickerLabel: 'Send invitations from',
  pickMine: 'Your calendar',
  pickBoard: 'Board account',
  pickGivmo: 'Email from Givmo',
  pickGivmoDescription: (orgName: string) => `As ${orgName} Board`,
  pickNone: 'Don’t send invitations',
  videoNoteGoogle: 'Leave it empty and Google Calendar adds a Meet link.',
  // Approved (CBG-FIX42, row 3.15).
  givmoNote:
    'Everyone invited gets an email with the invitation. Changes and a cancellation follow the same way.',
  scheduleAndSend: 'Schedule and send invitations',

  // ── The meeting page ───────────────────────────────────────────────────────────────────────────
  rowLabel: 'Invitations',
  // Approved (CBG-FIX42, rows 3.1 and 3.2): the count is the served `invited_count`, everyone the
  // invitation went to, less anyone removed from it in the calendar, and never the organizer's own
  // seat, which gets no invitation (CBG-FIX183, D-CBG-50; the backend counts it, History's sent
  // line alike).
  sentFrom: (email: string, count: number) => `From ${email} to ${people(count)}`,
  sentGivmo: (count: number) => `By email from Givmo to ${people(count)}`,
  // Approved (CBG-FIX36, row 2.1): `check_failed`, under the sent line. The page's re-read of the
  // calendar didn't finish inside its budget, so the page shows Board's stored record.
  checkFailed: (provider: string) =>
    `Board couldn’t check ${provider} just now, so a change made there may not show here yet.`,
  // Approved (CBG-FIX36, row 2.2): `out_of_step`, under the sent line. A change made in Board (the
  // meeting, or who's on the board) hasn't reached the calendar yet; opening the meeting sends it.
  outOfStep: (provider: string) =>
    `Board’s latest change hasn’t reached ${provider} yet. Board tries again when this meeting is next opened.`,
  // Approved (CBG-FIX36, row 2.3): the same, for a meeting Givmo emails.
  outOfStepGivmo:
    'Board’s latest change hasn’t been emailed to everyone invited yet. Board tries again when this meeting is next opened.',
  notSent: 'Not sent',
  sendInvitations: 'Send invitations',
  /** The backend's history note for a detached link, verbatim. */
  detached: 'No longer kept in step with a calendar',
  fix: 'Fix',
  scheduleAgain: 'Schedule it again',
  attention: {
    reconnect_required: (email: string) => `Board can’t reach ${email}`,
    event_missing: (provider: string) => `Board can’t find this meeting in ${provider}`,
    organizer_left: (name: string) => `${name} no longer has access to Board`,
    restored: (provider: string) => `This meeting is back in ${provider}.`,
    // Approved (CBG-FIX42, row 3.16): an old invitation Board can't prove is gone. It names no
    // address: the meeting's block doesn't carry the OLD organizer's.
    old_invitation_not_cancelled: 'Everyone invited may still have the earlier invitation',
    // Approved (D-CBG-50; CBG-FIX181, the dev live check's F3): the event at Board's id is no
    // longer Board's own copy (its marker gone, or another calendar organizes it), so it was moved
    // there.
    marker_missing: (provider: string) =>
      `This meeting was moved to another calendar in ${provider}`,
    // Approved (CBG-CAL1-COPY1): every code with no drawn words (the README's build note). B2.
    other: 'Something needs attention',
  },
  // Approved (D-CBG-50; CBG-FIX181): under the moved line, in every meeting state: Board can't
  // cancel it there.
  movedNote: 'Board can no longer change or cancel it there.',
  // Approved (CBG-CAL1-COPY1): `old_invitation_not_cancelled`'s act (Send retries the cancel). A1.
  sendTheCancellation: 'Send the cancellation',
  /** The officer's own remedy for that line (CBG-FIX32's question 6). Approved (CBG-FIX42, row 3.17). */
  directorsTold: 'I’ve told everyone',
  // Approved (CBG-CAL1-COPY1): the act beside "Something needs attention". B2.
  sendAgainAct: 'Send again',
  /** "1 time, in Google Calendar": where the last change was made, when a calendar made it. */
  rescheduledIn: (times: number, provider: string) =>
    times === 1
      ? `1 time, in ${provider}`
      : // Approved (CBG-CAL1-COPY1): more than one change, the last made in a calendar. B3.
        `${times} times, most recently in ${provider}`,
  historyTitle: 'History',
  historyScheduled: 'Scheduled',
  // Approved (CBG-CAL1-COPY1): Board's own reschedule as a history line (C1). C1.
  historyRescheduled: 'Rescheduled',
  /** The drawn line without its date, while the calendar's own instant isn't served (A3). */
  timeChangedIn: (provider: string) => `Time changed in ${provider}`,
  /** Question 9, approved with the mockups: "Time changed in Google Calendar on Oct 3" (CBG-FIX32). */
  timeChangedOn: (provider: string, day: string) => `Time changed in ${provider} on ${day}`,
  // Approved (CBG-CAL1-COPY1): the history glyph's accessible name, never shown. R2-4.
  historyNoteInfo: 'What the record says',
  repliedInCalendar: (day: string) => `Replied in their calendar ${day}`,
  noInvitation: 'No invitation',
  removedInCalendar: (provider: string, day: string) => `Removed in ${provider} ${day}`,
  inviteAgain: 'Invite again',

  // ── What needs attention: the dialogs ──────────────────────────────────────────────────────────
  fixReconnectTitleOwner: 'Reconnect your calendar',
  fixReconnectBody: (email: string) =>
    `Google stopped letting Board use ${email}. Until it’s reconnected, Board can’t update this meeting there, and changes made there don’t reach Board.`,
  // Approved (CBG-CAL1-COPY1): the Fix dialog for anyone but the calendar's owner. R2-2.
  fixReconnectTitleOthers: 'Board can’t reach this meeting’s calendar',
  // Approved (CBG-CAL1-COPY1): as above. R2-2.
  fixReconnectOthersNote: (name: string) =>
    `Only ${name} can reconnect it. Until then, you can send this meeting from another calendar.`,
  fixMissingTitle: (provider: string) => `Board can’t find this meeting in ${provider}`,
  fixMissingBody: (email: string) =>
    `It’s no longer in the calendar of ${email}. It may have been deleted there, or moved to a calendar Board can’t see. Board hasn’t cancelled anything.`,
  // Approved (D-CBG-50; CBG-FIX181): the moved meeting's Fix dialog. It offers no Send again: the
  // moved event stands, so a new one would be a second meeting.
  fixMovedBody: (provider: string) =>
    `Everyone invited still has it in their calendar. To change or cancel it, use ${provider}.`,
  // Approved (D-CBG-50): the moved meeting's Fix dialog's button (`send_from: none`). Its own
  // word: the send-from dialog's same act keeps "Stop keeping it in step" (`sendFromButton.none`).
  fixMovedDetach: 'Stop updating it',
  sendAgain: 'Send again',
  // Approved (CBG-FIX42, row 3.18).
  sendAgainNote: 'Everyone invited gets a new invitation from the same calendar.',
  cancelMeeting: 'Cancel meeting',

  /**
   * The handover's title, "Move Elena Fischer’s meetings". Approved (CBG-FIX111, row 3.2a): with no
   * name for the departed officer, the label of the act that opens it, never their address.
   */
  moveTitle: (name: string | null) => (name === null ? 'Move meetings' : `Move ${name}’s meetings`),
  /**
   * Its first line. Approved (CBG-FIX111, row 3.2b): with no name, Board's words for a name it can't
   * show, so the address appears once.
   */
  moveBody: (name: string | null, email: string, count: number) =>
    `${name ?? 'Someone whose name isn’t shown'} no longer has access to Board, and ${meetingsCount(count)} still go${count === 1 ? 'es' : ''} out from ${email}:`,
  moveTo: 'Send them from',
  // Approved (CBG-FIX42, row 3.19).
  moveNote: 'Everyone invited gets one cancellation from the old calendar and one new invitation.',
  moveButton: (count: number) => `Move ${count} meeting${count === 1 ? '' : 's'}`,

  disconnectTitle: 'Disconnect your calendar?',
  // Approved (CBG-CAL1-COPY1): the board account's or another officer's calendar that sends meetings. B10b.
  disconnectTitleOther: (email: string) => `Disconnect ${email}?`,
  disconnectBody: (count: number) =>
    `Your calendar sends ${count} upcoming board meeting${count === 1 ? '' : 's'}. What should happen to ${count === 1 ? 'it' : 'them'}?`,
  // Approved (CBG-CAL1-COPY1): as B10b.
  disconnectBodyOther: (count: number) =>
    `This calendar sends ${count} upcoming board meeting${count === 1 ? '' : 's'}. What should happen to ${count === 1 ? 'it' : 'them'}?`,
  // Approved (CBG-CAL1-COPY1): the choice group's accessible name, never shown. R2-4.
  disconnectGroup: 'What happens to it',
  disconnectMoveBoard: (count: number) =>
    count === 1
      ? 'Move it to the board account'
      : // The drawn line's other number (R2-5; listed on the copy-round page).
        'Move them to the board account',
  // Approved (CBG-FIX42, row 3.20).
  disconnectMoveBoardNote:
    'Everyone invited gets one cancellation from you and one new invitation.',
  // Approved (CBG-FIX42, row 3.21): someone else's calendar, whose cancellation isn't from you.
  disconnectMoveBoardNoteOther:
    'Everyone invited gets one cancellation from this calendar and one new invitation.',
  disconnectGivmo: (count: number) =>
    count === 1
      ? 'Send it by email from Givmo instead'
      : // The drawn line's other number (R2-5; listed on the copy-round page).
        'Send them by email from Givmo instead',
  // Approved (CBG-FIX42, row 3.22).
  disconnectGivmoNote: (orgName: string) =>
    `Everyone invited gets one cancellation from you and one new invitation from ${orgName} Board.`,
  // Approved (CBG-FIX42, row 3.23): someone else's calendar, whose cancellation isn't from you.
  disconnectGivmoNoteOther: (orgName: string) =>
    `Everyone invited gets one cancellation from this calendar and one new invitation from ${orgName} Board.`,
  disconnectLeave: (count: number, own: boolean) =>
    count === 1
      ? own
        ? 'Leave it in your calendar'
        : // Approved (CBG-CAL1-COPY1): someone else's calendar. B10b.
          'Leave it in that calendar'
      : own
        ? // The drawn line's other number (R2-5; listed on the copy-round page).
          'Leave them in your calendar'
        : // Approved (CBG-CAL1-COPY1): someone else's calendar, the other number. B10b.
          'Leave them in that calendar',
  disconnectLeaveNote: (count: number) =>
    count === 1
      ? 'It stays there as it is, and Board stops keeping it in step.'
      : // The drawn line's other number (R2-5; listed on the copy-round page).
        'They stay there as they are, and Board stops keeping them in step.',

  // ── A move that didn't finish, in the dialog that ran it (CBG-FIX36) ─────────────────────────
  // Approved (CBG-FIX36, row 2.4): the disconnect's title. Nothing was disconnected: a disconnect
  // never leaves a meeting on a dead connection.
  stillConnected: 'Your calendar is still connected',
  // Approved (CBG-FIX36, row 2.4): another officer's calendar, or the board account.
  stillConnectedOther: 'This calendar is still connected',
  /**
   * Approved (CBG-FIX36, row 2.5): the line above the meetings left. "2 of 3 meetings moved. Board
   * couldn't finish moving this one:"; with none moved, "Board couldn't finish moving this meeting:".
   */
  moveUnfinished: (moved: number, total: number, left: number) =>
    moved > 0
      ? `${moved} of ${total} meetings moved. Board couldn’t finish moving ${left === 1 ? 'this one' : 'these'}:`
      : `Board couldn’t finish moving ${left === 1 ? 'this meeting' : 'these meetings'}:`,
  // Approved (CBG-FIX36, row 2.6): under a meeting the move left where it was. One that moved with
  // something unfinished says its meeting page's own line instead.
  notMoved: 'Not moved',

  sendFromAnotherTitle: 'Send from another calendar',
  sendFromAnotherBody: (email: string) => `This meeting’s invitations come from ${email}.`,
  // Approved (CBG-CAL1-COPY1): the same line for a meeting Givmo emails (drawn only for an address). R3-2.
  sendFromAnotherBodyGivmo: (orgName: string) =>
    `This meeting’s invitations come from ${orgName} Board.`,
  // Approved (CBG-FIX42, row 3.24).
  sendFromAnotherMoveNote: 'Everyone invited gets one cancellation and one new invitation.',
  // Approved (CBG-FIX42, row 3.25).
  sendFromAnotherGivmoNote: (orgName: string) =>
    `Everyone invited gets one cancellation and one new invitation from ${orgName} Board.`,
  sendFromAnotherDetach: 'Stop keeping it in step',
  // Approved (CBG-FIX42, row 3.26).
  sendFromAnotherDetachNote: (provider: string) =>
    `It stays in ${provider} as it is, and Board stops updating it. No one gets anything new.`,
  sendFromButton: {
    board_account: 'Send from the board account',
    // Approved (CBG-CAL1-COPY1): B9.
    my_calendar: 'Send from your calendar',
    // Approved (CBG-CAL1-COPY1): B9.
    givmo_email: 'Send by email from Givmo',
    // Approved (CBG-CAL1-COPY1): B9.
    none: 'Stop keeping it in step',
  } as Readonly<Record<string, string>>,

  // ── Errors (the shipped fixed line for a failure that isn't the backend's; B8) ───────────────
  failed: 'We couldn’t record that. Please try again.',

  // ── The director's line ────────────────────────────────────────────────────────────────────────
  directorSentFrom: (email: string) =>
    `An invitation for this meeting was sent to you from ${email}.`,
  directorSentGivmo: (orgName: string) =>
    `An invitation for this meeting was emailed to you from ${orgName} Board.`,

  // ── The Meetings list's "How this works" (the plan's call 1, verbatim) ────────────────────────
  howCountInCalendar:
    'For a change made in your calendar, Board counts from when your calendar recorded the change.',
} as const;
